import React, { Fragment } from 'react'
import { graphql } from 'gatsby'
import Img from 'gatsby-image'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, rem, lines, selection } from '../../styles/tools'

import { AutoFade, AutoSlide } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import StaticVideo from '../../components/StaticVideo'

import Logo from '../../assets/pages/work/cbs/Logo.js'
import loop from '../../assets/pages/work/cbs/loop.mp4'

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/cbs.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/cbs.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/cbs.jpg" }) {
      ...twitterShareImage
    }
    cbsTvBbtBotm: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-bbt-botm.png" }
    ) {
      ...smallImage
    }
    cbsTvBbtTop: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-bbt-top.png" }
    ) {
      ...smallImage
    }
    cbsTvBottom: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-bottom.png" }
    ) {
      ...smallImage
    }
    cbsTvMenuMid: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-menu-mid.png" }
    ) {
      ...smallImage
    }
    cbsTvNcis1: file(relativePath: { eq: "pages/work/cbs/cbs-tv-ncis1.png" }) {
      ...smallImage
    }
    cbsTvNcis2: file(relativePath: { eq: "pages/work/cbs/cbs-tv-ncis2.png" }) {
      ...smallImage
    }
    cbsTvSearchMid: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-search-mid.png" }
    ) {
      ...smallImage
    }
    cbsTvShowsTop: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-shows-top.png" }
    ) {
      ...smallImage
    }
    cbsTvTwoGirls: file(
      relativePath: { eq: "pages/work/cbs/cbs-tv-two-girls.png" }
    ) {
      ...smallImage
    }
    parallaxImg: file(relativePath: { eq: "pages/work/cbs/parallax-img.png" }) {
      ...smallImage
    }
    showTitles: file(relativePath: { eq: "pages/work/cbs/show-titles.png" }) {
      ...smallImage
    }
    tvDisplay: file(relativePath: { eq: "pages/work/cbs/tv-display.png" }) {
      ...smallImage
    }
  }
`

let project = getProject('cbs')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo />,
  }

  const cbsTvBbtBotm = data.cbsTvBbtBotm.childImageSharp.fluid
  const cbsTvBbtTop = data.cbsTvBbtTop.childImageSharp.fluid
  const cbsTvBottom = data.cbsTvBottom.childImageSharp.fluid
  const cbsTvMenuMid = data.cbsTvMenuMid.childImageSharp.fluid
  const cbsTvNcis1 = data.cbsTvNcis1.childImageSharp.fluid
  const cbsTvNcis2 = data.cbsTvNcis2.childImageSharp.fluid
  const cbsTvSearchMid = data.cbsTvSearchMid.childImageSharp.fluid
  const cbsTvShowsTop = data.cbsTvShowsTop.childImageSharp.fluid
  const cbsTvTwoGirls = data.cbsTvTwoGirls.childImageSharp.fluid
  const parallaxImg = data.parallaxImg.childImageSharp.fluid
  const showTitles = data.showTitles.childImageSharp.fluid
  const tvDisplay = data.tvDisplay.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <Fragment>
            <AutoFade>
              <Title as="h1" css={{ maxWidth: '14em' }}>
                Visualizing America's Most Watched Network
              </Title>
            </AutoFade>
            <Space y={32} />
            <AutoFade>
              <Paragraph>
                Before their subscription service, CBS All Access debuted on
                Roku, CBS challenged us with conceptualizing what a “universal
                streaming app” could look like regardless of platform. Working
                with the in-house design team, our goal was to create a visual
                concept that showcased the best way to present CBS content on
                any OTT app. Our approach was to break away from templates that
                platforms like Roku typically provide. We created a custom UI
                for CBS with a consistent user and visual experience across
                multiple platforms.
              </Paragraph>
            </AutoFade>
          </Fragment>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <AutoSlide up css={{ marginBottom: '-25vw', position: 'relative' }}>
          <Img fluid={tvDisplay} />
          <StaticVideo
            src={loop}
            css={{
              width: '90.2%',
              position: 'absolute',
              top: '3.2%',
              left: '4.5%',
            }}
            ratio={768 / 1366}
          />
        </AutoSlide>
      </ProjectIntro>
      <Wrapper padded>
        <Space y="25vw" />
        <Space y={64} />
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          CONCEPT
        </BackgroundText>
        <AutoFade css={{ marginTop: '-7vw' }}>
          <Img fluid={cbsTvNcis1} />
        </AutoFade>
        <Space xl />
        <AutoFade>
          <Img fluid={cbsTvNcis2} />
        </AutoFade>
        <Space y={{ mobile: 64, tabletMd: 128 }} />
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <Wrapper css={{ height: '130%', userSelect: 'none' }}>
            <Img fluid={showTitles} css={{ height: '100%' }} />
          </Wrapper>
        }
      >
        <div
          css={{
            height: '55vh',
            maxHeight: rem(800),
            [media.tabletMd]: { height: '95vh' },
          }}
        />
      </Parallax>
      <Wrapper padded>
        <Space y={{ mobile: 64, tabletMd: 128 }} />
        <div css={{ position: 'relative' }}>
          <AutoSlide up>
            <Img
              fluid={cbsTvBbtBotm}
              ratio={619 / 1024}
              style={{ width: '88%', marginLeft: '-2%' }}
            />
          </AutoSlide>
          <AutoSlide
            up
            delay={200}
            css={{
              width: '88%',
              position: 'absolute',
              top: '18%',
              left: '14.9%',
            }}
          >
            <Img fluid={cbsTvBbtTop} />
          </AutoSlide>
        </div>
        <Space y={{ mobile: 64, tabletMd: 128 }} />
        <Space y={32} />
        <div
          css={{
            position: 'relative',
            left: '4%',
          }}
        >
          <AutoFade>
            <Img fluid={cbsTvBottom} />
          </AutoFade>
          <AutoSlide
            left
            delay={1 * 400}
            css={{
              width: '36%',
              position: 'absolute',
              top: '-8%',
              left: '-12%',
            }}
          >
            <Img fluid={cbsTvMenuMid} />
          </AutoSlide>
          <AutoSlide
            left
            delay={2 * 400}
            css={{
              width: '36%',
              position: 'absolute',
              top: '7%',
              left: '12%',
            }}
          >
            <Img fluid={cbsTvSearchMid} />
          </AutoSlide>
          <AutoSlide
            left
            delay={3 * 400}
            css={{
              width: '33%',
              position: 'absolute',
              top: '25%',
              left: '24%',
            }}
          >
            <Img fluid={cbsTvShowsTop} />
          </AutoSlide>
        </div>
        <Space y={{ mobile: 64, tabletMd: 128 }} />
        <AutoFade>
          <Img fluid={cbsTvTwoGirls} />
        </AutoFade>
        <Space y={{ mobile: 64, tabletMd: 128 }} />
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <Img
            fluid={parallaxImg}
            css={{ height: '130%', userSelect: 'none' }}
          />
        }
      >
        <div
          css={{
            height: '55vh',
            maxHeight: rem(800),
            [media.tabletMd]: { height: '95vh' },
          }}
        />
      </Parallax>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <div
            css={[
              lines('rgba(255, 255, 255, .06)'),
              {
                display: 'flex',
                alignItems: 'center',
                height: '130%',
                userSelect: 'none',
                backgroundColor: project.color,
              },
            ]}
          >
            <div
              css={{
                width: '80%',
                top: '10%',
                position: 'relative',
                left: '-9%',
                [media.tabletLg]: { width: '45%', top: '35%' },
              }}
            >
              <svg viewBox="0 0 56.373 56.373" fill="#0d345e">
                <path d="M56.373,28.187c0,15.567-12.62,28.187-28.187,28.187S0,43.754,0,28.187S12.62,0,28.187,0S56.373,12.62,56.373,28.187z M54.199,27.323c-5.8-10.07-14.48-15.26-26.26-15.77c-1.4,0.15-3,0.24-4.59,0.52c-9.62,1.72-16.64,7-21.3,15.55c-0.152,0.347-0.152,0.743,0,1.09c5.757,11.554,18.444,17.928,31.15,15.65c9.45-1.71,16.36-7.06,21-15.45C54.518,28.431,54.518,27.805,54.199,27.323z M28.139,13.053c-8.356,0-15.13,6.774-15.13,15.13s6.774,15.13,15.13,15.13s15.13-6.774,15.13-15.13S36.495,13.053,28.139,13.053z" />
              </svg>
            </div>
          </div>
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '65vh',
              maxHeight: rem(600),
              textAlign: 'center',
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title as="blockquote" css={{ maxWidth: '18em' }}>
              Our design exploration laid the groundwork for cord cutters to
              enjoy their favorite CBS programs whenever, wherever!
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
